// ---------------------------------------------------------------------------
// ChannelCardSparkline — sparkline with a title on the left and a 4-item
// metric selector on the right.
//
// Header layout (one flex row):
//   {METRIC} / MONTH · {PERIOD}                 [Int.][Posts][Likes][Com.]
//
// The human-readable date range (periodLabel, e.g. "Jan 2026 — Mar 2026") is
// exposed as the title's hover tooltip so it stays discoverable without
// cluttering the header.
//
// Selection is local state, defaulting to 'interactions' on every fresh
// mount. No localStorage — Q12 locks no-persistence for the metric selector.
// The parent provides data for all 4 metrics (dataByMetric) so switching
// is instant and does not fire a network request.
//
// The metric selector uses the Radix ToggleGroup primitive
// (@/components/ui/toggle-group) to match the site's standard segmented
// look (see QualityToggle under discovery/filters for the reference
// pattern). That gives us a dark-tinted container with a subtle cyan fill
// on the active item instead of four independent saturated pills.
// ---------------------------------------------------------------------------

import React, { useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { MiniPostsChart } from '../MiniPostsChart';
import type { ChannelMiniChartEntry } from '../types';
import type { TimePeriod } from '@/components/v11/discovery/types';

export type SparklineMetric = 'interactions' | 'posts' | 'likes' | 'comments';

export interface ChannelCardSparklineData {
  readonly interactions: readonly ChannelMiniChartEntry[];
  readonly posts: readonly ChannelMiniChartEntry[];
  readonly likes: readonly ChannelMiniChartEntry[];
  readonly comments: readonly ChannelMiniChartEntry[];
}

export interface ChannelCardSparklineProps {
  readonly dataByMetric: ChannelCardSparklineData;
  /** Currently selected period — used to build the title's period segment. */
  readonly period: TimePeriod;
  /** Optional human-readable date range — surfaced as the title tooltip. */
  readonly periodLabel?: string;
}

const PILLS: ReadonlyArray<{ readonly metric: SparklineMetric; readonly label: string }> = [
  { metric: 'interactions', label: 'Interactions' },
  { metric: 'posts', label: 'Posts' },
  { metric: 'likes', label: 'Likes' },
  { metric: 'comments', label: 'Comments' },
];

const METRIC_TITLE: Readonly<Record<SparklineMetric, string>> = {
  interactions: 'INTERACTIONS',
  posts: 'POSTS',
  likes: 'LIKES',
  comments: 'COMMENTS',
};

const PERIOD_TITLE: Readonly<Record<TimePeriod, string>> = {
  today: 'TODAY',
  yesterday: 'YEST.',
  '7d': '7D',
  '15d': '15D',
  '30d': '30D',
  '3m': '3M',
  '6m': '6M',
  '12m': '12M',
  wtd: 'WTD',
  mtd: 'MTD',
  qtd: 'QTD',
  ytd: 'XTD',
  all: 'ALL',
  custom: 'CUSTOM',
};

export function ChannelCardSparkline({
  dataByMetric,
  period,
  periodLabel,
}: ChannelCardSparklineProps) {
  const [selected, setSelected] = useState<SparklineMetric>('interactions');
  const chartData = dataByMetric[selected];
  const title = `${METRIC_TITLE[selected]} / MONTH · ${PERIOD_TITLE[period]}`;
  const tooltip = periodLabel ? `Showing data from ${periodLabel}` : undefined;

  // Radix ToggleGroup "type=single" requires exactly one value. Refuse
  // empty changes so the user can't deselect and leave the chart
  // without a source metric.
  const handleChange = (value: string) => {
    if (!value) return;
    setSelected(value as SparklineMetric);
  };

  return (
    <div className="px-4 py-2 border-t border-v11-border">
      <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
        <p
          title={tooltip}
          className="text-[10px] font-semibold text-v11-text-muted tracking-wider tabular-nums whitespace-nowrap"
        >
          {title}
        </p>
        <ToggleGroup
          type="single"
          value={selected}
          onValueChange={handleChange}
          aria-label="Sparkline metric"
          className="ml-auto justify-end gap-0 border border-v11-border rounded-md overflow-hidden bg-v11-bg-secondary"
        >
          {PILLS.map(({ metric, label }, i) => (
            <ToggleGroupItem
              key={metric}
              value={metric}
              aria-label={label}
              className={`data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:bg-v11-surface-hover transition rounded-none text-[10px] font-medium px-2 h-6 ${
                i < PILLS.length - 1 ? 'border-r border-v11-border/30' : ''
              }`}
            >
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <MiniPostsChart data={chartData} />
    </div>
  );
}
